import { Button, Card, Heading, Input, Text, toast } from "@overmap-ai/blocks"
import { OrganizationAccessLevel, ERROR_MSG_UNEXPECTED } from "@overmap-ai/common"
import { APIError, selectProjectById } from "@overmap-ai/core"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { type FocusEventHandler, memo, useCallback, useMemo } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { TextConfirmationDialog } from "../../components/Dialogs/TextConfirmationDialog"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { RouteLinksCard } from "../../components/RouteLinksCard/RouteLinksCard"
import type { RouteLinksCardRoute } from "../../components/RouteLinksCard/typings"
import { useUserOrganizationAccess } from "../../hooks/users"
import { useBlurInput } from "../../hooks/utils"
import { Layer, Minimap, Source } from "../../mapbox/components"
import { mapStyleColorMapping } from "../../mapbox/mappings"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { setActiveProjectId, useAppDispatch, useAppSelector } from "../../store"
import { selectActiveProjectId, selectMapStyle } from "../../store/selectors"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/projects/$projectId")

const routes: RouteLinksCardRoute[] = [
	{
		icon: modelIconMapping.user,
		label: "Members",
		description: "Manage members of the project",
		from: "/$organizationId/settings/projects/$projectId",
		to: "/$organizationId/settings/projects/$projectId/members",
	},
	{
		icon: modelIconMapping.attachment,
		label: "Attachments",
		description: "Manage attachments of the project",
		from: "/$organizationId/settings/projects/$projectId",
		to: "/$organizationId/settings/projects/$projectId/attachments",
	},
]

export const ProjectSettingsDetailView = memo(() => {
	const { projectId } = routeApi.useParams()

	const project = useAppSelector(selectProjectById(projectId))

	if (!project) {
		throw new Error(`Expected a project, got ${project}`)
	}

	const activeProjectId = useAppSelector(selectActiveProjectId)
	const isAdminUser = useUserOrganizationAccess(OrganizationAccessLevel.ADMIN)
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const mapStyle = useAppSelector(selectMapStyle)
	const color = mapStyleColorMapping[mapStyle]

	const handleProjectNameInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
		(e) => {
			const projectName = e.target.value

			if (!projectName) {
				toast.error({
					title: "Project name cannot be blank",
					description: "Please enter a valid project name",
				})
				return
			}

			sdk.projects.update({ ...project, name: projectName }).catch((error) => {
				toast.error({
					title: "Failed to update project name",
					description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
				})
			})
		},
		[project],
	)

	const handleDeleteProjectConfirmed = useCallback(async () => {
		await navigate({
			from: "/$organizationId/settings/projects/$projectId",
			to: "/$organizationId/settings/projects",
		})

		if (activeProjectId === project.uuid) dispatch(setActiveProjectId(null))

		sdk.projects.delete(project.uuid).catch((error) => {
			console.error("Failed to delete project:", error)
			toast.error({
				title: "Failed to delete project",
				description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
			})
		})
	}, [project.uuid, activeProjectId, dispatch, navigate])

	const projectNameInputProps = useBlurInput(project.name, handleProjectNameInputBlur, true)

	const paint = useMemo(() => {
		return { "line-color": color, "line-dasharray": [2, 4], "line-width": 1.5 }
	}, [color])

	return (
		<ViewContainer maxWidth="2xl">
			<section className="flex w-full flex-col gap-4">
				<div className="flex w-full flex-col gap-1">
					<Heading size="xl">{project.name}</Heading>
					<Text size="sm" accentColor="base">
						Manage project settings.
					</Text>
				</div>

				<Card className="flex flex-col gap-4" size="sm">
					<div className="flex w-max flex-col gap-1">
						<Text size="sm" accentColor="base">
							Name
						</Text>
						<Input.Root variant="outline" accentColor="primary" className="w-[300px]">
							<Input.Field
								placeholder="Enter a name"
								disabled={!isAdminUser}
								{...projectNameInputProps}
							/>
						</Input.Root>
					</div>

					<div className="flex w-full flex-col gap-1">
						<Text size="sm" accentColor="base">
							Bounds
						</Text>

						<Minimap geometry={project.bounds}>
							<Source type="geojson" data={project.bounds}>
								<Layer type="line" paint={paint} />
							</Source>
						</Minimap>
					</div>
				</Card>

				<RouteLinksCard routes={routes} />

				<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
					<TextConfirmationDialog
						title="Confirm project deletion"
						description={`This will irrevocably delete "${project.name}" along with everything associated with it.`}
						confirmMessage={`To confirm deletion, enter "${project.name}" in the field.`}
						confirmString={`${project.name}`}
						onConfirm={handleDeleteProjectConfirmed}
						actionLabel="Delete"
					>
						<Button className="w-fit" accentColor="danger" size="sm">
							Delete
						</Button>
					</TextConfirmationDialog>
				</OrganizationAccessRequired>
			</section>
		</ViewContainer>
	)
})
ProjectSettingsDetailView.displayName = "ProjectSettingsDetailView"
